import ErrorBoundary from "@/components/common/ErrorBoundary";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/datatable/components/DataTable";
import { safeDecompress, tableSearchSchema } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useConfirm from "@/hooks/useConfirm";
import useIGoldTransactionsQuery from "@/hooks/useIGoldTransactionsQuery";
import { igoldTransactionsKeys } from "@/query-keys";
import type { IGoldTransactionListItem } from "@/schemas/igoldTransactionSchema";
import { services } from "@/services";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import NewIGoldTransaction from "./_components/NewIGoldTransaction";
import useColumns from "./_hooks/useColumns";
import useInternalGoldTransactionMutation from "./_hooks/useIGoldTransactionMutation";

export const Route = createFileRoute("/_private/igold-transactions/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: igoldTransactionsKeys.lists(deps.q),
      queryFn: () => services.getIGoldTransactions(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => (
    <ErrorBoundary error={error} reset={() => window.location.reload()} />
  ),
});

function RouteComponent() {
  const search = Route.useSearch();
  const loaderData = Route.useLoaderData();

  const columns = useColumns();
  const { t } = useTranslation();

  const { data: paginatedTransactions } = useIGoldTransactionsQuery({
    q: search.q!,
    initialData: loaderData,
  });

  const { deleteTransaction, isDeleting } = useInternalGoldTransactionMutation();

  const deleteConfirm = useConfirm({
    title: t("igoldTransactions.confirm.delete.title"),
    description: t("igoldTransactions.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleDelete = useCallback(
    (igoldTransaction: IGoldTransactionListItem) =>
      deleteConfirm.confirm(() => void deleteTransaction(igoldTransaction.id)),
    [deleteConfirm, deleteTransaction],
  );

  return (
    <>
      <PageHeader
        title={t("igoldTransactions.list.title")}
        subtitle={t("igoldTransactions.list.subtitle")}
        action={<NewIGoldTransaction />}
      />
      <DataTable<IGoldTransactionListItem>
        searchPlaceholder={t("igoldTransactions.list.searchPlaceholder")}
        columns={columns}
        data={paginatedTransactions?.items}
        nextCursor={paginatedTransactions?.nextCursor}
        hasMore={paginatedTransactions?.hasMore}
        queryKey={igoldTransactionsKeys.lists(search.q)}
        navigateFrom="/igold-transactions"
        searchFrom="/_private/igold-transactions/"
        onDelete={handleDelete}
        searchable={true}
      />
      {deleteConfirm.confirmDialog}
    </>
  );
}
